import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AlertCircle, ArrowLeft, Clock, Send } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  useQuiz,
  useQuizAttempt,
  useQuizQuestions,
  useSubmitQuizAttempt,
} from '@/hooks/useQuizzes'
import {
  QuizQuestion,
  QuizQuestionFileUpload,
  QuizQuestionType,
  QuizFileUploadRule,
  QuizAttemptAnswerSelection,
  QuizAttemptAnswerNumeric,
  QuizAttemptAnswerText,
  QuizAttemptAnswerMap,
} from '@/types/quizzes'
import { QuizAttemptModel, QuizModel } from '@/models/quiz.model'
import { paths } from '@/routes/path'
import { detectDeviceType } from '@/utils/device'
import { labelForQuizQuestionType } from '@/utils/quiz'

type AnswerState =
  | {
    type: 'mcq'
    selected: string | null
  }
  | {
    type: 'multi'
    selected: Set<string>
  }
  | {
    type: 'numeric'
    value: string
  }
  | {
    type: 'short_answer'
    text: string
  }
  | {
    type: 'file_upload'
  }

const getFileUploadRules = (
  metadata: QuizQuestionFileUpload['metadata'],
): QuizFileUploadRule | undefined => {
  if (!metadata || typeof metadata !== 'object') return undefined
  const candidate = metadata as Partial<QuizFileUploadRule>
  if (
    'maxFiles' in candidate ||
    'allowedMimeTypes' in candidate ||
    'maxFileSizeBytes' in candidate ||
    'instructions' in candidate
  ) {
    return candidate as QuizFileUploadRule
  }
  return undefined
}

interface StartState {
  attempt: QuizAttemptModel
  quiz: QuizModel
  questions: QuizQuestion[]
}

export function QuizAttemptTakePage() {
  const { quizId, attemptId, courseId } = useParams<{
    quizId: string
    attemptId: string
    courseId: string
  }>()
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = location.state as StartState | undefined

  const { data: quizFallback, isLoading: isQuizLoading } = useQuiz(quizId)
  const { data: attemptFallback, isLoading: isAttemptLoading } = useQuizAttempt(quizId, attemptId)
  const { data: questionsFallback, isLoading: isQuestionsLoading } = useQuizQuestions(quizId)
  const submitAttempt = useSubmitQuizAttempt(quizId)

  const quiz = locationState?.quiz ?? quizFallback
  const attempt = locationState?.attempt ?? attemptFallback
  const questions = locationState?.questions ?? questionsFallback?.questions ?? quiz?.questions ?? []

  const [answers, setAnswers] = useState<Record<string, AnswerState>>({})
  const [fileUploads, setFileUploads] = useState<Record<string, File[]>>({})
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [autoSubmitting, setAutoSubmitting] = useState(false)

  useEffect(() => {
    if (!attempt) return
    const initial: Record<string, AnswerState> = {}
    attempt.answers?.questions?.forEach((item) => {
      switch (item.type) {
        case 'mcq': {
          const selected =
            (item.answer as QuizAttemptAnswerSelection | undefined)?.selected ?? []
          initial[item.id] = { type: 'mcq', selected: selected[0] ?? null }
          break
        }
        case 'multi': {
          const selected =
            (item.answer as QuizAttemptAnswerSelection | undefined)?.selected ?? []
          initial[item.id] = {
            type: 'multi',
            selected: new Set(selected),
          }
          break
        }
        case 'numeric': {
          const value = (item.answer as QuizAttemptAnswerNumeric | undefined)?.value
          initial[item.id] = {
            type: 'numeric',
            value:
              value !== null && value !== undefined ? value.toString() : '',
          }
          break
        }
        case 'short_answer': {
          const text = (item.answer as QuizAttemptAnswerText | undefined)?.text ?? ''
          initial[item.id] = { type: 'short_answer', text }
          break
        }
        case 'file_upload':
          initial[item.id] = { type: 'file_upload' }
          break
        default:
          break
      }
    })
    questions.forEach((question) => {
      if (!initial[question.id]) {
        switch (question.type) {
          case 'mcq':
            initial[question.id] = { type: 'mcq', selected: null }
            break
          case 'multi':
            initial[question.id] = { type: 'multi', selected: new Set() }
            break
          case 'numeric':
            initial[question.id] = { type: 'numeric', value: '' }
            break
          case 'short_answer':
            initial[question.id] = { type: 'short_answer', text: '' }
            break
          case 'file_upload':
            initial[question.id] = { type: 'file_upload' }
            break
          default:
            break
        }
      }
    })
    setAnswers(initial)
  }, [attempt, questions])

  useEffect(() => {
    if (!quiz || !attempt || !quiz.timeLimitSeconds) {
      setTimeRemaining(null)
      return
    }
    const startedAtMs = new Date(attempt.startedAt).getTime()
    const deadline = startedAtMs + quiz.timeLimitSeconds * 1000

    const update = () => {
      const remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000))
      setTimeRemaining(remaining)
      if (remaining <= 0) {
        setAutoSubmitting(true)
      }
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [quiz, attempt])

  useEffect(() => {
    if (autoSubmitting && !submitAttempt.isPending) {
      handleSubmit(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmitting])

  if (!quizId || !attemptId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Attempt not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              Please return to the course quiz list and start a new attempt.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isQuizLoading || isAttemptLoading || isQuestionsLoading || !quiz || !attempt) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (attempt.status === 'submitted' || attempt.status === 'graded') {
    navigate(paths.app.quizzes.learnerAttemptDetail.build(courseId ?? '', quizId, attemptId), {
      replace: true,
    })
    return null
  }

  const handleSelectOption = (questionId: string, option: string) => {
    setAnswers((prev) => {
      const current = prev[questionId]
      if (!current) return prev
      if (current.type === 'mcq') {
        return { ...prev, [questionId]: { type: 'mcq', selected: option } }
      }
      if (current.type === 'multi') {
        const next = new Set(current.selected)
        if (next.has(option)) {
          next.delete(option)
        } else {
          next.add(option)
        }
        return { ...prev, [questionId]: { type: 'multi', selected: next } }
      }
      return prev
    })
  }

  const handleNumericChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { type: 'numeric', value },
    }))
  }

  const handleShortAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { type: 'short_answer', text: value },
    }))
  }

  const handleFileChange = (
    questionId: string,
    event: React.ChangeEvent<HTMLInputElement>,
    question: QuizQuestionFileUpload,
  ) => {
    const files = Array.from(event.target.files ?? [])
    const rules = getFileUploadRules(question.metadata)
    const maxFiles = rules?.maxFiles ?? files.length
    const limited = files.slice(0, maxFiles)
    setFileUploads((prev) => ({ ...prev, [questionId]: limited }))
  }

  const handleSubmit = async (autoSubmit = false) => {
    if (submitAttempt.isPending) return
    if (!quiz || !attempt) return
    try {
      const submittedAt = new Date()
      const durationSeconds = Math.max(
        0,
        Math.floor(
          (submittedAt.getTime() - new Date(attempt.startedAt).getTime()) / 1000,
        ),
      )
      const answersPayload = {
        quizId: quiz.id,
        attempt: attempt.attempt,
        startedAt: attempt.startedAt,
        submittedAt: submittedAt.toISOString(),
        durationSeconds,
        questions: questions.map((question) => {
          const answerState = answers[question.id]
          const mapped: QuizAttemptAnswerMap = {
            id: question.id,
            type: question.type,
            answer: null,
          }

          if (!answerState) {
            return mapped
          }

          switch (question.type) {
            case 'mcq': {
              const selectedOption =
                answerState.type === 'mcq' && answerState.selected
                  ? [answerState.selected]
                  : []
              mapped.answer = selectedOption.length ? { selected: selectedOption } : null
              break
            }
            case 'multi': {
              const selectedMany =
                answerState.type === 'multi'
                  ? Array.from(answerState.selected)
                  : []
              mapped.answer = selectedMany.length ? { selected: selectedMany } : null
              break
            }
            case 'numeric': {
              const numericValue =
                answerState.type === 'numeric' && answerState.value
                  ? Number(answerState.value)
                  : null
              const validNumeric =
                numericValue !== null && !Number.isNaN(numericValue)
              mapped.answer = validNumeric ? { value: numericValue! } : null
              break
            }
            case 'short_answer': {
              const textAnswer =
                answerState.type === 'short_answer'
                  ? answerState.text.trim()
                  : ''
              mapped.answer = textAnswer ? { text: textAnswer } : null
              break
            }
            case 'file_upload':
              mapped.answer = {
                files: (fileUploads[question.id] ?? []).map((file) => ({
                  filename: file.name,
                })),
              }
              break
            default:
              mapped.answer = null
          }

          return mapped
        }),
        meta: {
          device: detectDeviceType(),
          userAgent: window.navigator.userAgent,
          submittedAutomatically: autoSubmit,
        },
      }

      const uploadedFiles = Object.values(fileUploads).flat()

      await submitAttempt.mutateAsync({
        quizId: quiz.id,
        attemptId: attempt.id,
        answers: answersPayload,
        meta: answersPayload.meta ?? undefined,
        files: uploadedFiles,
      })

      toast.success(autoSubmit ? 'Time is up. Your answers have been submitted.' : 'Quiz submitted successfully.')
      navigate(
        paths.app.quizzes.learnerAttemptDetail.build(courseId ?? '', quiz.id, attempt.id),
        { replace: true },
      )
    } catch (error) {
      console.error(error)
      toast.error('We could not submit your quiz. Please try again.')
    } finally {
      if (autoSubmit) {
        setAutoSubmitting(false)
      }
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="gap-2 p-0 text-sm text-gray-600"
            onClick={() => navigate(paths.app.quizzes.courseList.build(courseId ?? ''), { replace: true })}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to quizzes
          </Button>
          {timeRemaining !== null && (
            <Badge variant={timeRemaining < 60 ? 'destructive' : 'outline'} className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {formatTime(timeRemaining)}
            </Badge>
          )}
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{quiz.title}</h1>
        <p className="max-w-2xl text-gray-600">
          Work through each question carefully. You can submit early whenever you are satisfied with your answers.
        </p>
      </header>

      {questions.map((question, index) => (
        <Card key={question.id} className="border border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-lg">
              <span>
                Question {index + 1}{' '}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {labelForQuizQuestionType(question.type)} · {question.scoreWeight ?? 0} pts
                </span>
              </span>
              <Badge variant="outline">{question.type.replace('_', ' ')}</Badge>
            </CardTitle>
            {question.metadata && question.type === 'short_answer' && (
              <CardDescription>
                {(question.metadata as any)?.hint ?? 'Provide a concise response.'}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-900">{question.questionText}</p>
            {renderQuestionInput({
              question,
              answerState: answers[question.id],
              fileUploads: fileUploads[question.id] ?? [],
              onSelectOption: handleSelectOption,
              onNumericChange: handleNumericChange,
              onShortAnswerChange: handleShortAnswerChange,
              onFileChange: handleFileChange,
            })}
          </CardContent>
        </Card>
      ))}

      <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <AlertCircle className="h-5 w-5" />
        <p>
          Double-check your responses before submitting. Once submitted, you cannot change your answers unless your instructor allows another attempt.
        </p>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button
          className="flex items-center gap-2"
          onClick={() => handleSubmit(false)}
          disabled={submitAttempt.isPending}
        >
          <Send className="h-4 w-4" />
          {submitAttempt.isPending ? 'Submitting...' : 'Submit quiz'}
        </Button>
      </div>
    </div>
  )
}

function renderQuestionInput(args: {
  question: QuizQuestion
  answerState: AnswerState | undefined
  fileUploads: File[]
  onSelectOption: (questionId: string, option: string) => void
  onNumericChange: (questionId: string, value: string) => void
  onShortAnswerChange: (questionId: string, value: string) => void
  onFileChange: (questionId: string, event: React.ChangeEvent<HTMLInputElement>, question: QuizQuestionFileUpload) => void
}) {
  const { question, answerState, fileUploads, onSelectOption, onNumericChange, onShortAnswerChange, onFileChange } = args
  switch (question.type) {
    case 'mcq':
    case 'multi': {
      const selectedValues =
        answerState?.type === 'multi'
          ? answerState.selected
          : answerState?.type === 'mcq'
            ? new Set(answerState.selected ? [answerState.selected] : [])
            : new Set<string>()
      return (
        <div className="space-y-3">
          {(question.options ?? []).map((option) => (
            <label
              key={option.key}
              className="flex cursor-pointer items-center gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:border-primary"
            >
              <input
                type={question.type === 'multi' ? 'checkbox' : 'radio'}
                name={question.id}
                value={option.key}
                checked={selectedValues.has(option.key)}
                onChange={() => onSelectOption(question.id, option.key)}
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>
      )
    }
    case 'numeric':
      return (
        <Input
          type="number"
          step="0.01"
          value={answerState?.type === 'numeric' ? answerState.value : ''}
          onChange={(event) => onNumericChange(question.id, event.target.value)}
          onPaste={(e) => e.preventDefault()}
          placeholder="Enter your answer"
        />
      )
    case 'short_answer':
      return (
        <Textarea
          rows={5}
          value={answerState?.type === 'short_answer' ? answerState.text : ''}
          onChange={(event) => onShortAnswerChange(question.id, event.target.value)}
          onPaste={(e) => e.preventDefault()}
          placeholder="Write your response"
        />
      )
    case 'file_upload': {
      const typedQuestion = question as QuizQuestionFileUpload
      const rules = getFileUploadRules(typedQuestion.metadata)
      return (
        <div className="space-y-2">
          <Input
            type="file"
            multiple={(rules?.maxFiles ?? 1) > 1}
            accept={
              Array.isArray(rules?.allowedMimeTypes)
                ? rules?.allowedMimeTypes.join(',')
                : undefined
            }
            onChange={(event) => onFileChange(typedQuestion.id, event, typedQuestion)}
          />
          {fileUploads.length > 0 && (
            <ul className="space-y-1 text-sm text-gray-600">
              {fileUploads.map((file) => (
                <li key={`${typedQuestion.id}-${file.name}`}>{file.name}</li>
              ))}
            </ul>
          )}
          {rules?.instructions && (
            <p className="text-xs text-gray-500">{rules.instructions}</p>
          )}
        </div>
      )
    }
    default:
      return null
  }
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
